import { Component, EventEmitter, Input, Output } from '@angular/core';

/** A page number, or an ellipsis gap in the windowed pager. */
export type MapsPageToken = number | 'ellipsis';

/**
 * Client-side pager for the results grid (7-14-26 redesign). Windowed page
 * numbers (1 … current±1 … last) keep the bar compact even when an admin
 * raises the result count, plus a results-per-page selector whose choice the
 * parent persists for the session.
 */
@Component({
    selector: 'maps-pagination',
    standalone: false,
    templateUrl: './pagination.component.html',
    styleUrl: './pagination.component.scss',
})
export class PaginationComponent {
    @Input({ required: true }) currentPage!: number;
    @Input({ required: true }) totalItems!: number;
    @Input({ required: true }) pageSize!: number;
    @Input() pageSizeOptions: readonly number[] = [10, 20, 50];
    /** Noun in the range text ("candidate", "message"). Regular plurals only. */
    @Input() itemNoun = 'candidate';

    @Output() pageChange = new EventEmitter<number>();
    @Output() pageSizeChange = new EventEmitter<number>();

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    }

    /** Current page clamped to the valid range (totalItems can shrink under us). */
    get safePage(): number {
        return Math.min(Math.max(1, this.currentPage), this.totalPages);
    }

    /**
     * Windowed page tokens: always show first + last, the current page ±1,
     * and collapse each gap to a single ellipsis.
     */
    get pageTokens(): MapsPageToken[] {
        const total = this.totalPages;
        const current = this.safePage;
        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }
        const pages = new Set<number>([1, total, current - 1, current, current + 1]);
        const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
        const tokens: MapsPageToken[] = [];
        let prev = 0;
        for (const p of sorted) {
            if (prev > 0 && p - prev > 1) tokens.push('ellipsis');
            tokens.push(p);
            prev = p;
        }
        return tokens;
    }

    IsNumber(token: MapsPageToken): token is number {
        return token !== 'ellipsis';
    }

    /** "Showing 1–10 of 32 candidates · page 1 of 4" (mockup copy; the page
     *  clause drops on a single page). Pure function of the inputs. */
    get rangeText(): string {
        const total = this.totalItems;
        if (total === 0) return `Showing 0 ${this.itemNoun}s`;
        const start = (this.safePage - 1) * this.pageSize + 1;
        const end = Math.min(total, this.safePage * this.pageSize);
        const base = `Showing ${start}–${end} of ${total} ${total === 1 ? this.itemNoun : `${this.itemNoun}s`}`;
        return this.totalPages > 1 ? `${base} · page ${this.safePage} of ${this.totalPages}` : base;
    }

    GoTo(page: number): void {
        if (page < 1 || page > this.totalPages || page === this.safePage) return;
        this.pageChange.emit(page);
    }

    OnSizeSelect(event: Event): void {
        const value = Number((event.target as HTMLSelectElement).value);
        if (Number.isFinite(value) && value > 0) {
            this.pageSizeChange.emit(value);
        }
    }

    TrackByToken(index: number, token: MapsPageToken): string {
        return token === 'ellipsis' ? `e${index}` : `p${token}`;
    }
}
